/** Item de OS na ótica do cálculo de valor (subset do que o serviço devolve). */
export interface ItemOSParaCalculo {
  quantidade: number | null;
  valor_total: number;
  servico: { tipo_preco: string; valor_base: number } | null;
}

export interface ResultadoValorFinal {
  valorFinal: number;
  valorDiferenca: number;
  temServicosPorHora: boolean;
}

/** Formata minutos em "N minutos" / "N horas" / "Nh Mmin". */
export function formatarTempo(minutos: number): string {
  if (minutos < 60) {
    return `${minutos} minuto${minutos !== 1 ? 's' : ''}`;
  }
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  if (mins === 0) {
    return `${horas} hora${horas !== 1 ? 's' : ''}`;
  }
  return `${horas}h ${mins}min`;
}

/** Arredonda um valor monetário a cêntimos (2 casas decimais). */
export function arredondarCentimos(valor: number): number {
  return Math.round(valor * 100) / 100;
}

/**
 * Recalcula o valor final considerando serviços por hora.
 * Serviços por_hora cobram proporcional EXATO: (minutosFaturaveis/60) × valor_base,
 * arredondado a cêntimos — sem mínimo de horas, sem arredondamento a 0.5h.
 * Serviços fixos/m² mantêm o valor_total. Sem itens, devolve o valor original.
 * `minutosFaturaveis` são as horas confirmadas pelo prestador (não o cronómetro de parede).
 */
export function calcularValorFinal(params: {
  itensOS: ItemOSParaCalculo[] | null | undefined;
  minutosFaturaveis: number;
  valorOriginal: number;
  jaConcluida?: boolean;
}): ResultadoValorFinal {
  const { itensOS, minutosFaturaveis, valorOriginal, jaConcluida = false } = params;

  if (!itensOS || itensOS.length === 0) {
    return { valorFinal: valorOriginal, valorDiferenca: 0, temServicosPorHora: false };
  }

  const horasFaturaveis = minutosFaturaveis / 60;
  let total = 0;
  let temServicosPorHora = false;

  for (const item of itensOS) {
    if (item.servico?.tipo_preco === 'por_hora') {
      temServicosPorHora = true;
      total += arredondarCentimos(horasFaturaveis * item.servico.valor_base);
    } else {
      total += item.valor_total;
    }
  }

  // OS reaberta (já paga): nunca recalcula o valor. Mantém o original;
  // temServicosPorHora continua refletido para a UI.
  if (jaConcluida) {
    return { valorFinal: valorOriginal, valorDiferenca: 0, temServicosPorHora };
  }

  const valorFinal = arredondarCentimos(total);
  return { valorFinal, valorDiferenca: arredondarCentimos(valorFinal - valorOriginal), temServicosPorHora };
}

/**
 * Tempo operacional a gravar em `tempo_servico_minutos` (métrica da visita).
 * OS nova: o tempo desta ronda (cronómetro de parede), mínimo 1 minuto.
 * OS reaberta (correção/garantia): PRESERVA o tempo original — o retrabalho não
 * acumula nem altera a métrica do serviço; fica só no evento de auditoria.
 */
export function calcularTempoTotal(params: {
  tempoAnterior: number | null | undefined;
  elapsedMinutos: number;
  jaConcluida?: boolean;
}): number {
  const anterior = params.tempoAnterior ?? 0;
  if (params.jaConcluida) {
    return Math.max(1, anterior);
  }
  return Math.max(1, params.elapsedMinutos);
}
